<template>
    <div class="checklist-templates">
        <div class="page-header">
            <div class="header-left">
                <h2>{{ $t("auto.checklistTemplates") }}</h2>
                <p class="description">{{ $t("auto.defineChecklistTemplatesForTasks") }}</p>
            </div>
        </div>

        <div v-if="loading" class="loading-container">
            <a-spin size="large" />
        </div>

        <div v-else class="templates-container">
            <!-- Templates List -->
            <div class="templates-list">
                <div
                    v-for="template in templates"
                    :key="template.id"
                    class="template-card"
                    :class="{ 'is-default': template.isDefault }"
                >
                    <div class="template-header">
                        <div class="template-header-left">
                            <span class="template-name">{{ template.name }}</span>
                            <a-tag v-if="template.isDefault" color="green">{{ $t("auto.default") }}</a-tag>
                        </div>
                        <div class="template-header-right">
                            <a-tooltip :title="template.isDefault ? 'Varsayılandan kaldır' : 'Varsayılan olarak ayarla'">
                                <a-button
                                    type="text"
                                    size="small"
                                    class="icon-button"
                                    :class="{ active: template.isDefault }"
                                    @click="setAsDefault(template.id)"
                                >
                                    <i class="material-icons">{{ template.isDefault ? "star" : "star_border" }}</i>
                                </a-button>
                            </a-tooltip>
                            <a-button type="text" size="small" class="icon-button" @click="editTemplate(template)">
                                <i class="material-icons">edit</i>
                            </a-button>
                            <a-button
                                type="text"
                                size="small"
                                danger
                                class="icon-button"
                                @click="confirmDeleteTemplate(template)"
                            >
                                <i class="material-icons">delete</i>
                            </a-button>
                        </div>
                    </div>

                    <div class="template-body">
                        <div class="items-preview">
                            <div v-for="(item, index) in template.items.slice(0, 5)" :key="index" class="item-row">
                                <i class="material-icons">check_box_outline_blank</i>
                                <span>{{ item.title }}</span>
                            </div>
                            <div v-if="template.items.length > 5" class="more-items">
                                {{ $t("auto.moreItemsCount", { count: template.items.length - 5 }) }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Add Template Button -->
            <div class="add-template-btn" @click="createNewTemplate">
                <i class="material-icons">add_circle</i>
                <span>{{ $t("auto.addNewTemplate") }}</span>
            </div>
        </div>

        <!-- Template Editor Modal -->
        <a-modal
            :visible="showEditor"
            :title="
                editingTemplate && editingTemplate.id
                    ? $t('task.checklistTemplateModalEdit')
                    : $t('task.checklistTemplateModalNew')
            "
            width="600px"
            :footer="null"
            @cancel="closeEditor"
            :bodyStyle="{ padding: '24px' }"
            wrapClassName="template-editor-modal"
        >
            <div v-if="editingTemplate" class="template-editor">
                <div class="form-group">
                    <label>{{ $t("auto.templateName") }}</label>
                    <a-input v-model="editingTemplate.name" :placeholder="$t('auto.enterTemplateName')" size="large" />
                </div>

                <div class="form-group">
                    <label>{{ $t("auto.checklistItems") }}</label>
                    <div class="items-list">
                        <div v-for="(item, index) in editingTemplate.items" :key="index" class="item-edit-row">
                            <i class="material-icons drag-handle">drag_indicator</i>
                            <a-input v-model="item.title" :placeholder="$t('auto.itemTitle')" />
                            <a-button type="text" size="small" danger @click="removeItem(index)">
                                <i class="material-icons">close</i>
                            </a-button>
                        </div>
                    </div>
                    <div class="add-item-btn" @click="addItem">
                        <i class="material-icons">add</i>
                        <span>{{ $t("auto.addItem") }}</span>
                    </div>
                </div>

                <div class="form-group">
                    <a-checkbox v-model="editingTemplate.isDefault"> {{ $t("auto.setThisTemplateAsDefault") }} </a-checkbox>
                </div>

                <div class="modal-footer">
                    <a-button @click="closeEditor">{{ $t("auto.cancel") }}</a-button>
                    <a-button type="primary" @click="saveTemplate" :disabled="!canSave"> {{ $t("auto.save") }} </a-button>
                </div>
            </div>
        </a-modal>
    </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import _ from "lodash";

export default {
    name: "ChecklistTemplates",
    props: {
        workflowId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            showEditor: false,
            editingTemplate: null,
            loading: false,
        };
    },
    computed: {
        ...mapState({
            workflows: (state) => state.tasks.workflows,
        }),
        currentWorkflow() {
            return this.workflows.find((w) => w._id === this.workflowId);
        },
        templates() {
            if (!this.currentWorkflow || !this.currentWorkflow.checklistTemplates) {
                return [];
            }
            return this.currentWorkflow.checklistTemplates;
        },
        canSave() {
            return (
                this.editingTemplate &&
                this.editingTemplate.name &&
                this.editingTemplate.name.trim().length > 0 &&
                this.editingTemplate.items &&
                this.editingTemplate.items.length > 0 &&
                this.editingTemplate.items.every(function (item) {
                    return item.title && item.title.trim().length > 0;
                })
            );
        },
    },
    methods: {
        ...mapActions({
            updateWorkflow: "tasks/updateWorkflow",
        }),

        generateUUID() {
            return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
                var r = (Math.random() * 16) | 0;
                var v = c === "x" ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            });
        },

        createNewTemplate() {
            this.editingTemplate = {
                id: this.generateUUID(),
                name: "",
                items: [{ title: "" }],
                isDefault: this.templates.length === 0,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };
            this.showEditor = true;
        },

        editTemplate(template) {
            this.editingTemplate = _.cloneDeep(template);
            this.showEditor = true;
        },

        closeEditor() {
            this.showEditor = false;
            this.editingTemplate = null;
        },

        addItem() {
            if (!this.editingTemplate.items) {
                this.editingTemplate.items = [];
            }
            this.editingTemplate.items.push({ title: "" });
        },

        removeItem(index) {
            this.editingTemplate.items.splice(index, 1);
        },

        async saveTemplate() {
            if (!this.canSave) return;

            this.loading = true;
            try {
                var templates = _.cloneDeep(this.templates);
                var existingIndex = templates.findIndex((t) => t.id === this.editingTemplate.id);

                // Eğer bu şablon varsayılan olacaksa, diğerlerinin varsayılanını kaldır
                if (this.editingTemplate.isDefault) {
                    templates = templates.map(function (t) {
                        return Object.assign({}, t, { isDefault: false });
                    });
                }

                this.editingTemplate.updatedAt = new Date().toISOString();

                if (existingIndex !== -1) {
                    templates[existingIndex] = this.editingTemplate;
                } else {
                    templates.push(this.editingTemplate);
                }

                await this.updateWorkflow({
                    workflowId: this.workflowId,
                    params: { checklistTemplates: templates },
                });

                this.$message.success("Şablon kaydedildi");
                this.closeEditor();
            } catch (error) {
                console.error("saveTemplate error", error);
                this.$message.error("Şablon kaydedilirken hata oluştu");
            } finally {
                this.loading = false;
            }
        },

        async setAsDefault(templateId) {
            var template = this.templates.find(function (t) {
                return t.id === templateId;
            });
            var isCurrentlyDefault = template && template.isDefault;

            this.loading = true;
            try {
                var templates = _.cloneDeep(this.templates);
                templates = templates.map(function (t) {
                    var willBeDefault = isCurrentlyDefault ? false : t.id === templateId;
                    return Object.assign({}, t, {
                        isDefault: willBeDefault,
                        updatedAt: t.id === templateId ? new Date().toISOString() : t.updatedAt,
                    });
                });

                await this.updateWorkflow({
                    workflowId: this.workflowId,
                    params: { checklistTemplates: templates },
                });

                this.$message.success(isCurrentlyDefault ? "Varsayılan şablon kaldırıldı" : "Varsayılan şablon güncellendi");
            } catch (error) {
                console.error("setAsDefault error", error);
                this.$message.error("İşlem sırasında hata oluştu");
            } finally {
                this.loading = false;
            }
        },

        confirmDeleteTemplate(template) {
            var self = this;
            this.$confirm({
                title: "Şablonu Sil",
                content: '"' + template.name + '" şablonunu silmek istediğinizden emin misiniz?',
                okText: "Sil",
                okType: "danger",
                cancelText: "İptal",
                onOk: function () {
                    return self.deleteTemplate(template.id);
                },
            });
        },

        async deleteTemplate(templateId) {
            this.loading = true;
            try {
                var templates = this.templates.filter(function (t) {
                    return t.id !== templateId;
                });

                await this.updateWorkflow({
                    workflowId: this.workflowId,
                    params: { checklistTemplates: templates },
                });

                this.$message.success("Şablon silindi");
            } catch (error) {
                console.error("deleteTemplate error", error);
                this.$message.error("Şablon silinirken hata oluştu");
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss">
.template-editor-modal {
    .template-editor {
        .form-group {
            margin-bottom: 24px;

            label {
                display: block;
                font-weight: 600;
                margin-bottom: 8px;
                color: #1a1a1a;
            }
        }

        .items-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
            margin-bottom: 12px;

            .item-edit-row {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px;
                background: #fafafa;
                border-radius: 6px;

                .drag-handle {
                    cursor: grab;
                    color: #999;
                    font-size: 20px;

                    &:active {
                        cursor: grabbing;
                    }
                }

                .ant-input {
                    flex: 1;
                }
            }
        }

        .add-item-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 10px;
            background: #ffffff;
            border: 1px dashed #d9d9d9;
            border-radius: 6px;
            cursor: pointer;
            color: #1890ff;
            transition: all 0.3s ease;

            &:hover {
                background: #e6f7ff;
                border-color: #1890ff;
            }

            .material-icons {
                font-size: 18px;
            }
        }

        .modal-footer {
            display: flex;
            justify-content: flex-end;
            gap: 12px;
            padding-top: 16px;
            border-top: 1px solid #f0f0f0;
        }
    }
}
</style>
<style lang="scss">
.checklist-templates {
    padding: 24px;
    max-width: 1000px;

    .page-header {
        margin-bottom: 32px;
        padding-bottom: 24px;
        border-bottom: 2px solid #f0f0f0;

        .header-left {
            h2 {
                font-size: 24px;
                font-weight: 600;
                color: #1a1a1a;
                margin: 0 0 8px 0;
            }

            .description {
                color: #666;
                font-size: 14px;
                margin: 0;
            }
        }
    }

    .loading-container {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 300px;
    }

    .templates-container {
        .templates-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
            margin-bottom: 16px;
        }

        .template-card {
            background: #ffffff;
            border: 2px solid #e8e8e8;
            border-radius: 12px;
            padding: 20px;
            transition: all 0.3s ease;

            &:hover {
                border-color: #1890ff;
                box-shadow: 0 4px 12px rgba(24, 144, 255, 0.1);
            }

            &.is-default {
                border-color: #52c41a;
                background: #f6ffed;
            }

            .template-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 16px;

                .template-header-left {
                    display: flex;
                    align-items: center;
                    gap: 12px;

                    .template-name {
                        font-size: 16px;
                        font-weight: 600;
                        color: #1a1a1a;
                    }
                }

                .template-header-right {
                    display: flex;
                    align-items: center;
                    gap: 8px;

                    .icon-button {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        padding: 6px;
                        width: 32px;
                        height: 32px;
                        border-radius: 6px;
                        transition: all 0.2s ease;

                        .material-icons {
                            font-size: 18px;
                            line-height: 1;
                        }

                        &:hover {
                            background-color: rgba(0, 0, 0, 0.04);
                        }

                        &.active .material-icons {
                            color: #faad14;
                        }

                        &.ant-btn-dangerous:hover {
                            background-color: rgba(255, 77, 79, 0.1);

                            .material-icons {
                                color: #ff4d4f;
                            }
                        }
                    }
                }
            }

            .template-body {
                .items-preview {
                    .item-row {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        padding: 6px 0;
                        font-size: 14px;
                        color: #666;

                        .material-icons {
                            font-size: 18px;
                            color: #999;
                        }
                    }

                    .more-items {
                        padding: 6px 0;
                        font-size: 13px;
                        color: #999;
                        font-style: italic;
                    }
                }
            }
        }

        .add-template-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 20px;
            background: #ffffff;
            border: 2px dashed #1890ff;
            border-radius: 12px;
            cursor: pointer;
            color: #1890ff;
            font-weight: 600;
            transition: all 0.3s ease;

            &:hover {
                background: #e6f7ff;
                border-color: #40a9ff;
            }

            .material-icons {
                font-size: 24px;
            }
        }
    }
}
</style>
